import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { InactivityService } from '@app/services/inactivity/inactivity.service';
import { GeneralDataService } from '@app/services/general/general-data.service';
import { OrquestadorService } from '@app/services/orquestador/orquestador.service';
import { OrquestadorInquilinoService } from '@app/tenant/services/OrquestadorInquilino/orquestador-inquilino.service';
import { buildInactivityBody, buildOrchestatorOptions } from '@app/utils/new-flow/inactivity.utils';
import { DEFAULTENANT, RESUMENPROCESO, SARLAFT_OTP_LISTNUMBERS } from '@app/routes.constants';
import { Store } from '@ngrx/store';
import { selectQuoteId, selectResumeTenantData } from '@app/tenant/storage/resume.selectors';
import { selectDataByKey } from 'src/storage/general.selectors';

@Component({
  selector: 'app-cotizacion-pausa-inactivo',
  templateUrl: './cotizacion-pausa-inactivo.component.html',
  styleUrl: './cotizacion-pausa-inactivo.component.scss'
})
export class CotizacionPausaInactivoComponent implements OnInit, OnDestroy {
  private inactivityService = inject(InactivityService);
  private generalDataService = inject(GeneralDataService);
  private orquestadorService = inject(OrquestadorService);
  private orquestadorInquilinoService = inject(OrquestadorInquilinoService);
  private router = inject(Router);
  private store = inject(Store);

  inactivityKeys: ('PolicyHolder' | 'Tenant' | 'Property' | 'ProductCode')[] = [];
  asesorCode: string = '';
  bitacoraId: string = '';
  private isTenantFlow: boolean = false;
  private isSarlaftFlow: boolean = false;
  quoteId$ = this.store.select(selectQuoteId);
  quoteId: string = '';

  ngOnInit(): void {
    // Detener cualquier timer previo antes de iniciar el segundo timer
    this.inactivityService.stopTimer();

    // Obtener las keys originales del servicio de inactividad
    this.inactivityKeys = this.inactivityService.getInactivityKeys();
    console.log('Keys recuperadas del servicio de inactividad:', this.inactivityKeys);

    // Detectar de qué flujo viene
    const originalUrl = this.inactivityService.getOriginalUrl();

    // Detectar flujo de tenant
    this.isTenantFlow = originalUrl.includes('/seguros/hogar/arriendo/inquilino/') ||
      (this.inactivityKeys.length === 1 && this.inactivityKeys[0] === 'Tenant');

    // Detectar flujo de sarlaft
    this.isSarlaftFlow = originalUrl.includes('/seguros/hogar/arriendo/sarlaft/') ||
      (this.inactivityKeys.length === 1 && this.inactivityKeys[0] === 'PolicyHolder');

    // Iniciar timer de segunda inactividad (30 segundos)
    this.inactivityService.startInactivityTimer(
      'seguros/hogar/arriendo/cotizador/tenemos-tus-datos',
      undefined,
      this.inactivityKeys,
      undefined,
      'Segunda inactividad - Pantalla de pausa',
      undefined,
      true, // useSecondTimer = true (30 segundos)
      undefined,
      'Cotización en Pausa' // Nombre de la pantalla
    );

    this.quoteId$.subscribe(id => {
      this.quoteId = id || '';
    });

    // Suscribirse al código de asesor

    this.store.select(selectDataByKey('codigoAsesor')).subscribe(code => {
      if (code) {
        console.log('Código de asesor desde el store:', code);
        this.asesorCode = code;
      }
    });
    
  }

  goBack(): void {
    console.log('Usuario regresando - Reseteando estados de inactividad');
    // Detener el timer antes de volver atrás
    this.inactivityService.stopTimer();
    // Resetear los estados de rescate y retoma
    this.inactivityService.resetStates();

    // Redirigir según el flujo detectado
    if (this.isTenantFlow) {
      console.log('Redirigiendo al flujo de tenant: resumen-proceso');
      const cleanQuoteId = decodeURIComponent(this.quoteId);
      this.router.navigate([DEFAULTENANT, RESUMENPROCESO], {
        queryParams: { quoteId: cleanQuoteId }
      });
    } else if (this.isSarlaftFlow) {
      console.log('Redirigiendo al flujo de sarlaft: sarlaft-otp-listnumbers');
      this.router.navigate([SARLAFT_OTP_LISTNUMBERS]);
    } else {
      // Flujo new-flow: usar history.back()
      window.history.back();
    }
  }

  /**
   * Método llamado cuando el usuario hace clic en "Contactar asesor"
   * Habilita el estado de rescate y realiza el llamado al orquestador
   */
  contactarAsesor(): void {
    console.log('Usuario contactando asesor - Habilitando estadoRescate y llamando al orquestador');
    console.log('🔍 Detectando flujo en contactar asesor:', this.isTenantFlow ? 'TENANT' : 'NEW-FLOW');

    // Detener el timer de inactividad
    this.inactivityService.stopTimer();

    // Habilitar el estado de rescate
    this.inactivityService.enableEstadoRescate();

    // Obtener datos de la pantalla original donde ocurrió la primera inactividad
    const originalScreenName = this.inactivityService.getOriginalScreenName();
    const originalUrl = this.inactivityService.getOriginalUrl();
    const originalLeadRequirement = this.inactivityService.getOriginalLeadRequirement();

    // Verificar si la pantalla original es "Datos-propietario" para enviar getLeadDuplicated
    const isDatosPropietarioScreen = originalScreenName === 'Datos-propietario';
    console.log('Pantalla original:', originalScreenName, '- Enviar getLeadDuplicated:', isDatosPropietarioScreen);

    if (this.isTenantFlow) {
      // Flujo de TENANT - usar orquestador de inquilino
      console.log('📤 Usando OrquestadorInquilinoService para contactar asesor');

      const body = {
        isInactivity: true,
        quoteId: "",
        estadoRescate: "",
        estadoRetoma: "",
        url: "",
        pantalla: "",
        leadRequirement: "",
        screenPercentage: "",
        FirstName: "",
        SecondName: "",
        LastName: "",
        SecondLastName: "",
        MobilePhone: "",
        Pais: "Colombia",
        TipoIdentificacion: "C",
        NumeroIdentificacion: "",
        FechaExpedicion: "",
        CorreoElectronico: ""
      }

      // Obtener quoteId del store de tenant
      this.store.select(selectQuoteId).subscribe(quoteId => {
        // const body = {
        //   isInactivity: true,
        //   quoteId: quoteId || '',
        //   leadStatus: '0_Descartado',
        //   leadRequirement: originalLeadRequirement || 'Contactar asesor - Rescate'
        // };

        body.isInactivity = true;
        body.quoteId = quoteId || '';
        body.estadoRescate = 'Habilitado';
        body.estadoRetoma = 'Inhabilitado';
        body.url = originalUrl || '';
        body.pantalla = originalScreenName || 'Sin nombre';
        body.leadRequirement = originalLeadRequirement || 'Contactar asesor - Rescate';
        body.screenPercentage = '35';

      }).unsubscribe();

      this.store.select(selectResumeTenantData).subscribe(resumeTenantData => {
        if (resumeTenantData) {
          body.FirstName = resumeTenantData.FirstName || '';
          body.SecondName = resumeTenantData.SecondName || '';
          body.LastName = resumeTenantData.LastName || '';
          body.SecondLastName = resumeTenantData.SecondLastName || '';
          body.MobilePhone = resumeTenantData.MobilePhone || '';
          body.NumeroIdentificacion = resumeTenantData.DocumentNumber || '';
          body.TipoIdentificacion = resumeTenantData.DocumentType || 'C';
          body.FechaExpedicion = resumeTenantData?.ExpeditionDate || '';
          body.CorreoElectronico = resumeTenantData.Mail || '';
        }
      }).unsubscribe();


      this.orquestadorInquilinoService.Lease_orchesResumesTenant(body).subscribe(
        (response) => {
          console.log('Respuesta del orquestador de tenant al contactar asesor:', response);
          // Navegar a la página de "tenemos tus datos"
          this.router.navigate(['seguros/hogar/arriendo/cotizador/tenemos-tus-datos']);
        },
        (error) => {
          console.error('Error al contactar asesor (tenant):', error);
          // Aunque haya error, redirigir al usuario
          this.router.navigate(['seguros/hogar/arriendo/cotizador/tenemos-tus-datos']);
        }
      );

    } else {
      // Flujo de NEW-FLOW - usar orquestador normal
      console.log('📤 Usando OrquestadorService para contactar asesor');

      // Preparar opciones para el body del orquestador
      const options = buildOrchestatorOptions(
        this.inactivityKeys,
        'Contactar asesor - Rescate',
        this.asesorCode,
        this.bitacoraId,
        undefined,
        undefined,
        isDatosPropietarioScreen // Solo enviar getLeadDuplicated si es Datos-propietario
      );

      // Generar el body y enviar al orquestador
      this.generalDataService.getbody(options).subscribe((body: any) => {
        console.log('Body generado para contactar asesor:', body);

        // Agregar los campos adicionales al body usando datos originales
        // Cuando el usuario hace click en "Contactar asesor":
        // - estadoRescate: Habilitado (el usuario quiere contacto)
        // - estadoRetoma: Inhabilitado (no fue abandono completo)
        // - leadRequirement: se mantiene el de la primera pantalla de inactividad
        const requestBody = buildInactivityBody(body, {
          estadoRescate: 'Habilitado',
          estadoRetoma: 'Inhabilitado',
          url: originalUrl || '',
          pantalla: originalScreenName || 'Sin nombre',
          leadRequirement: originalLeadRequirement,
          getLeadDuplicated: isDatosPropietarioScreen
        });

        console.log('Enviando al orquestador:', requestBody);

        // Enviar al orquestador
        this.orquestadorService.postOrquestador(requestBody).subscribe(
          (response) => {
            console.log('Respuesta del orquestador al contactar asesor:', response);

            // Solo verificar política de lead duplicados si se envió getLeadDuplicated
            if (isDatosPropietarioScreen && response?.data?.Politica === 'Politica de lead duplicados') {
              console.log('Lead duplicado detectado al contactar asesor - Redirigiendo a lo-sentimos');
              this.router.navigate(['seguros/hogar/arriendo/cotizador/lo-sentimos']);
              return;
            }

            // Navegar a la página de "tenemos tus datos"
            this.router.navigate(['seguros/hogar/arriendo/cotizador/tenemos-tus-datos']);
          },
          (error) => {
            console.error('Error al contactar asesor:', error);
            // Aunque haya error, redirigir al usuario
            this.router.navigate(['seguros/hogar/arriendo/cotizador/tenemos-tus-datos']);
          }
        );
      });
    }
  }

  ngOnDestroy(): void {
    // Detener el servicio de inactividad al destruir el componente
    this.inactivityService.stopTimer();
  }
}
